"use client";

import { localPointFromSvg } from "../scatter-svg";
import { ParentSize } from "@visx/responsive";
import { sankey, sankeyCenter, sankeyLinkHorizontal } from "@visx/sankey";
import type { Transition } from "motion/react";
import {
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useModuleRevealGate } from "@/components/motion/module-reveal-gate";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { cn } from "@/lib/utils";
import {
  type Margin,
  type SankeyLinkDatum,
  type SankeyNodeDatum,
  SankeyProvider,
  type SankeyTooltipData,
} from "./sankey-context";

export interface SankeyData {
  nodes: SankeyNodeDatum[];
  links: SankeyLinkDatum[];
}

export interface SankeyChartProps {
  /** Sankey data with nodes and links */
  data: SankeyData;
  /** Chart margins */
  margin?: Partial<Margin>;
  /** Animation duration in milliseconds. Default: 1100 */
  animationDuration?: number;
  /** Motion enter transition (spring or cubic-bezier tween). */
  enterTransition?: Transition;
  /** Signature of motion URL state — triggers enter replay when it changes. */
  revealSignature?: string;
  /** Aspect ratio as "width / height", or "auto" to fill the parent's height. Default: "2 / 1" */
  aspectRatio?: string;
  /** Node width in pixels. Default: 16 */
  nodeWidth?: number;
  /** Node padding in pixels. Default: 24 */
  nodePadding?: number;
  /** Additional class name for the container */
  className?: string;
  /** Child components (SankeyNode, SankeyLink, SankeyTooltip) */
  children: ReactNode;
  /** Controlled hovered node index (e.g. from ChartLegend). */
  hoveredNodeIndex?: number | null;
  /** Called when node hover changes from the chart surface. */
  onNodeHoverChange?: (index: number | null) => void;
}

const DEFAULT_MARGIN: Margin = { top: 40, right: 180, bottom: 40, left: 180 };

interface SankeyChartInnerProps {
  data: SankeyData;
  width: number;
  height: number;
  margin: Margin;
  animationDuration: number;
  enterTransition?: Transition;
  revealSignature?: string;
  nodeWidth: number;
  nodePadding: number;
  children: ReactNode;
  hoveredNodeIndexProp?: number | null;
  onNodeHoverChange?: (index: number | null) => void;
}

function SankeyChartInner(props: SankeyChartInnerProps) {
  const { width, height } = props;

  if (width < 10 || height < 10) {
    return null;
  }

  return <SankeyChartCore {...props} />;
}

const SankeyChartCore = memo(function SankeyChartCore({
  data,
  width,
  height,
  margin,
  animationDuration,
  enterTransition,
  revealSignature = "",
  nodeWidth,
  nodePadding,
  children,
  hoveredNodeIndexProp,
  onNodeHoverChange,
}: SankeyChartInnerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const tapPinRef = useRef<string | null>(null);
  const tapHandledRef = useRef(false);
  const hoverCapable = useHoverCapable();
  const revealGate = useModuleRevealGate();
  const holdReveal = revealGate === "held";
  const skipReveal = revealGate === "done";
  /**
   * Read through a ref, never as an effect dependency: the page marks itself
   * revealed while this reveal is still running, and a dependency would drop
   * the settle timer mid-flight — the chart would never reach `isLoaded`.
   */
  const skipRevealRef = useRef(skipReveal);
  skipRevealRef.current = skipReveal;
  const [isLoaded, setIsLoaded] = useState(skipReveal);
  const [revealEpoch, setRevealEpoch] = useState(0);
  const [internalHoveredNodeIndex, setInternalHoveredNodeIndex] = useState<
    number | null
  >(null);
  const isNodeHoverControlled = hoveredNodeIndexProp !== undefined;
  const hoveredNodeIndex = isNodeHoverControlled
    ? hoveredNodeIndexProp
    : internalHoveredNodeIndex;
  const setHoveredNodeIndex = useCallback(
    (index: number | null) => {
      if (isNodeHoverControlled) {
        onNodeHoverChange?.(index);
      } else {
        setInternalHoveredNodeIndex(index);
      }
    },
    [isNodeHoverControlled, onNodeHoverChange]
  );
  const [hoveredLinkIndex, setHoveredLinkIndex] = useState<number | null>(null);
  const [tooltipData, setTooltipData] = useState<SankeyTooltipData | null>(
    null
  );
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(
    null
  );

  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // biome-ignore lint/correctness/useExhaustiveDependencies: revealSignature
  useEffect(() => {
    // The page this chart sits on is not in view yet — the node and link
    // reveal waits instead of playing to an audience that cannot see it.
    if (holdReveal) {
      return;
    }
    if (skipRevealRef.current) {
      setIsLoaded(true);
      return;
    }
    setRevealEpoch((n) => n + 1);
    setIsLoaded(false);
    const timeout = setTimeout(() => {
      setIsLoaded(true);
    }, animationDuration);
    return () => clearTimeout(timeout);
  }, [animationDuration, holdReveal, revealSignature]);

  const sankeyGenerator = useMemo(() => {
    return sankey<SankeyNodeDatum, SankeyLinkDatum>()
      .nodeWidth(nodeWidth)
      .nodePadding(nodePadding)
      .nodeAlign(sankeyCenter)
      .extent([
        [0, 0],
        [innerWidth, innerHeight],
      ]);
  }, [innerWidth, innerHeight, nodeWidth, nodePadding]);

  const graph = useMemo(() => {
    const clonedData = {
      nodes: data.nodes.map((node) => ({ ...node })),
      links: data.links.map((link) => ({ ...link })),
    };
    return sankeyGenerator(clonedData);
  }, [data, sankeyGenerator]);

  const createPath = useCallback(
    // biome-ignore lint/suspicious/noExplicitAny: d3-sankey types are complex
    (link: any) => {
      try {
        const pathGenerator = sankeyLinkHorizontal();
        return pathGenerator(link) || "";
      } catch {
        return "";
      }
    },
    []
  );

  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    const point = localPointFromSvg(svgRef.current, event.clientX, event.clientY);
    if (point) {
      setMousePos({ x: point.x, y: point.y });
    }
  }, []);

  const clearInteraction = useCallback(() => {
    tapPinRef.current = null;
    setHoveredNodeIndex(null);
    setHoveredLinkIndex(null);
    setTooltipData(null);
    setMousePos(null);
  }, [setHoveredNodeIndex]);

  const handleMouseLeave = useCallback(() => {
    clearInteraction();
  }, [clearInteraction]);

  const pinTooltipFromTap = useCallback(
    (data: SankeyTooltipData, event: React.TouchEvent) => {
      tapHandledRef.current = true;
      const key = `${data.type}:${data.nodeIndex ?? data.linkIndex ?? -1}`;
      if (tapPinRef.current === key) {
        clearInteraction();
        return;
      }
      tapPinRef.current = key;

      const touch = event.touches[0];
      const svg = svgRef.current;
      const point =
        touch ? localPointFromSvg(svg, touch.clientX, touch.clientY) : null;
      setMousePos(point ? { x: point.x, y: point.y } : null);

      if (data.type === "node") {
        setHoveredLinkIndex(null);
        setHoveredNodeIndex(data.nodeIndex ?? null);
      } else {
        setHoveredNodeIndex(null);
        setHoveredLinkIndex(data.linkIndex ?? null);
      }
      setTooltipData(data);
    },
    [clearInteraction, setHoveredNodeIndex]
  );

  // Bubbles after the node/link handlers, so an unclaimed tap is empty chart
  // space and dismisses whatever is pinned.
  const handleTouchStart = useCallback(() => {
    if (tapHandledRef.current) {
      tapHandledRef.current = false;
      return;
    }
    clearInteraction();
  }, [clearInteraction]);

  useEffect(() => {
    const onDocPointerDown = (event: PointerEvent) => {
      if (event.pointerType !== "touch") {
        return;
      }
      if (tapPinRef.current === null) {
        return;
      }
      const svg = svgRef.current;
      const target = event.target as Node | null;
      if (svg && target && svg.contains(target)) {
        return;
      }
      clearInteraction();
    };
    document.addEventListener("pointerdown", onDocPointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", onDocPointerDown, true);
    };
  }, [clearInteraction]);

  const contextValue = {
    graph,
    nodes: graph.nodes,
    links: graph.links,
    width,
    height,
    innerWidth,
    innerHeight,
    margin,
    hoveredNodeIndex,
    hoveredLinkIndex,
    setHoveredNodeIndex,
    setHoveredLinkIndex,
    tooltipData,
    setTooltipData,
    containerRef,
    pinTooltipFromTap,
    isLoaded,
    animationDuration,
    enterTransition,
    revealEpoch,
    skipReveal,
    mousePos,
    createPath,
  };

  return (
    <SankeyProvider value={contextValue}>
      <div className="relative h-full w-full" ref={containerRef}>
        <svg
          aria-hidden="true"
          height={height}
          onMouseLeave={hoverCapable ? handleMouseLeave : undefined}
          onMouseMove={hoverCapable ? handleMouseMove : undefined}
          onTouchStart={handleTouchStart}
          ref={svgRef}
          style={{ touchAction: "manipulation" }}
          width={width}
        >
          <g transform={`translate(${margin.left},${margin.top})`}>
            {/* Held: nodes and links stay unmounted so their enter animation
                starts on the commit that releases the gate. */}
            {holdReveal ? null : children}
          </g>
        </svg>
      </div>
    </SankeyProvider>
  );
});

export function SankeyChart({
  data,
  margin: marginProp,
  animationDuration = 1100,
  enterTransition,
  revealSignature,
  aspectRatio = "2 / 1",
  nodeWidth = 16,
  nodePadding = 24,
  className = "",
  children,
  hoveredNodeIndex,
  onNodeHoverChange,
}: SankeyChartProps) {
  const margin = { ...DEFAULT_MARGIN, ...marginProp };
  const fillsParent = aspectRatio === "auto";

  return (
    <div
      className={cn("relative w-full", fillsParent && "h-full", className)}
      style={fillsParent ? undefined : { aspectRatio }}
    >
      <ParentSize>
        {({ width, height }) => (
          <SankeyChartInner
            animationDuration={animationDuration}
            data={data}
            enterTransition={enterTransition}
            height={height}
            hoveredNodeIndexProp={hoveredNodeIndex}
            margin={margin}
            nodePadding={nodePadding}
            nodeWidth={nodeWidth}
            onNodeHoverChange={onNodeHoverChange}
            revealSignature={revealSignature}
            width={width}
          >
            {children}
          </SankeyChartInner>
        )}
      </ParentSize>
    </div>
  );
}

SankeyChart.displayName = "SankeyChart";

export default SankeyChart;
